import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  ACCESSIBILITY_LABEL_HU,
  LOCATION_STATUS_LABEL_HU,
  LOCATION_TYPE_LABEL_HU,
  MAX_SHELF_LEVELS,
  MIN_SHELF_LEVELS,
  PALLET_STATUS_LABEL_HU,
  type LocationView,
} from '@persped/shared';
import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  useAddLevel,
  useLocation,
  useLocationFolders,
  useLockLevel,
  usePalletHistory,
  usePrintLocationLabels,
  useRemoveLevel,
  useUnlockLevel,
  useUpdateLocation,
} from '../../api/queries';
import { DismantleDialog } from './DismantleDialog';
import { EventTable } from '../../components/EventTable';
import { ProblemAlert } from '../../components/ProblemAlert';
import { formatTimestamp } from '../../format';

export function LocationDetailPage() {
  const { barcode = '' } = useParams();
  const location = useLocation(barcode);
  const folders = useLocationFolders(barcode);
  const palletHistory = usePalletHistory(barcode);
  const addLevel = useAddLevel();
  const removeLevel = useRemoveLevel();
  const lockLevel = useLockLevel();
  const unlockLevel = useUnlockLevel();
  const printLabel = usePrintLocationLabels();
  /**
   * MELYIK SZINT tartalmát mutatjuk az „Itt lévő mappák" listában
   * (2026-08-25-i igény). `null` = mind.
   *
   * Eddig a lista összevontan hozta a lokáció ÖSSZES mappáját, egy „Szint"
   * oszloppal — ami egy 5 szintes polcnál vagy egy megpakolt raklapnál
   * használhatatlan: a „mi van a 2. szinten" kérdésre szemmel kellett
   * kiválogatni a sorokat. A szintek táblájában egy sorra kattintva
   * mostantól rászűr.
   */
  const [szintSzuro, setSzintSzuro] = useState<number | null>(null);
  const [dismantle, setDismantle] = useState<{ levelNumber?: number; folderCount: number } | null>(
    null,
  );

  // A négy szintművelet EGY gombtiltást és EGY hibasávot oszt: egyszerre
  // úgyis csak az egyik futhat, és négy külön `isPending` a felületen csak
  // zajt adna.
  const szintMuvelet =
    addLevel.isPending || removeLevel.isPending || lockLevel.isPending || unlockLevel.isPending;
  const szintHiba =
    addLevel.error ?? removeLevel.error ?? lockLevel.error ?? unlockLevel.error ?? null;

  if (location.isPending) {
    return (
      <Stack sx={{ py: 6, alignItems: 'center' }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (location.isError) return <ProblemAlert error={location.error} />;

  const loc = location.data;

  // A szűrés a KLIENSEN történik, mert a lista amúgy is egyben jön le, és
  // egy lokációnak legfeljebb néhány száz mappája van. Egy szintenkénti
  // lekérdezés csak több kérést jelentene ugyanarra az adatra — és a
  // szintek közti váltás azonnal reagál.
  /**
   * A SZINTEK FENTRŐL LEFELÉ (2026-08-25-i igény) — ahogy a fizikai raklapon
   * és polcon állnak: a 3. szint fölül, alatta a 2., legalul az 1.
   *
   * ⚠ Csak a MEGJELENÍTÉS fordul meg. A `loc.levels` a szerver növekvő
   * sorrendjét őrzi, mert több helyen az `at(-1)` jelenti a legfelső szintet
   * — egy megfordított forrástömb ott csendben az ellenkezőjét jelentené.
   */
  const szintekFentrolLefele = [...(loc.levels ?? [])].sort(
    (a, b) => b.levelNumber - a.levelNumber,
  );
  const legfelsoSzint = loc.levels?.at(-1)?.levelNumber ?? 0;

  const szurtMappak = (folders.data ?? []).filter(
    (f) => szintSzuro === null || f.palletLevel === szintSzuro,
  );
  const isInactive = loc.status === 'INACTIVE';

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Button component={RouterLink} to="/lokaciok" startIcon={<ArrowBackIcon />}>
          Lokációk
        </Button>
        <Typography variant="h5" component="h2" sx={{ flexGrow: 1, fontFamily: 'monospace' }}>
          {loc.barcode}
        </Typography>
        {/*
          Címkenyomtatás onnan, ahol az objektum van (08-13 CR 8.2). Eddig
          csak a Címkék oldalról ment, ami egy frissen létrehozott raklapnál
          fölösleges kitérő volt.
        */}
        <Button
          variant="outlined"
          disabled={printLabel.isPending}
          onClick={() => printLabel.mutate({ barcodes: [loc.barcode] })}
        >
          Címke nyomtatása
        </Button>
        <Chip
          variant="outlined"
          color={isInactive ? 'default' : 'success'}
          label={LOCATION_STATUS_LABEL_HU[loc.status]}
        />
      </Stack>

      {printLabel.isError && <ProblemAlert error={printLabel.error} />}
      {printLabel.isSuccess && (
        <Alert severity="success" onClose={() => printLabel.reset()}>
          A címke nyomtatásra küldve.{' '}
          <RouterLink to={`/cimkek/${printLabel.data.id}`}>Köteg megnyitása</RouterLink>
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Row label="Név" value={loc.name} />
          <Row label="Típus" value={LOCATION_TYPE_LABEL_HU[loc.locationType]} />
          <Row label="Mappák száma" value={String(loc.folderCount)} />
          {loc.isDefaultStaging && (
            <Alert severity="info">
              Ez az alapértelmezett Előkészítő — nem deaktiválható, mert enélkül a regisztráció
              célhely nélkül maradna.
            </Alert>
          )}
        </Stack>
      </Paper>

      {/*
        POLC — a szintjei FIXEK, üresen is megmaradnak, és KÖZVETLENÜL
        fogadnak mappát. A G fázisig raklap is állhatott rajtuk; az
        egymásba ágyazás megszűnt, ezért a polcszint tartalma egyféle.

        A LEZÁRÁS csak innen, a webről érhető el (2026-08-14-i CR #14): a
        TC22-n egy félrenyúlás munka közben zárna le egy szintet.
      */}
      {loc.locationType === 'SHELF' && loc.levels && (
        <Paper variant="outlined">
          <Typography variant="subtitle1" sx={{ p: 2, pb: 1 }}>
            Polc — {loc.levels.length} szint
          </Typography>
          <Divider />
          {szintHiba && (
            <div style={{ padding: 16, paddingBottom: 0 }}>
              <ProblemAlert error={szintHiba} />
            </div>
          )}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="right">Szint</TableCell>
                  <TableCell align="right">Mappák</TableCell>
                  <TableCell>Elérhetőség</TableCell>
                  <TableCell>Állapot</TableCell>
                  <TableCell align="right">Műveletek</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {szintekFentrolLefele.map((level) => {
                  // ⚠ A „legfelső" a LEGNAGYOBB SORSZÁM, nem a lista utolsó
                  // eleme. Amíg a tábla növekvő sorrendben állt, a kettő
                  // egybeesett; a megfordítás után az indexre épülő
                  // feltétel az ELSŐ szintet hitte volna legfelsőnek, és a
                  // törlés gombot a rossz sorra tette volna.
                  const legfelso = level.levelNumber === legfelsoSzint;
                  return (
                    <TableRow
                      key={level.levelNumber}
                      hover
                      selected={szintSzuro === level.levelNumber}
                      sx={{ cursor: 'pointer' }}
                      onClick={() =>
                        setSzintSzuro((elozo) =>
                          elozo === level.levelNumber ? null : level.levelNumber,
                        )
                      }
                    >
                      <TableCell align="right">{level.levelNumber}</TableCell>
                      <TableCell align="right">{level.folderCount}</TableCell>
                      <TableCell>{ACCESSIBILITY_LABEL_HU[level.accessibility]}</TableCell>
                      <TableCell>
                        {level.isLocked ? (
                          <Chip size="small" color="warning" variant="outlined" label="Lezárva" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Betárolható
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            disabled={szintMuvelet}
                            onClick={() =>
                              (level.isLocked ? unlockLevel : lockLevel).mutate({
                                barcode: loc.barcode,
                                levelNumber: level.levelNumber,
                              })
                            }
                          >
                            {level.isLocked ? 'Feloldás' : 'Lezárás'}
                          </Button>
                          {/*
                            Csak a LEGFELSŐ, ÜRES szint törölhető. A polc
                            szintjei fizikai polcok: egy közbenső eltávolítása
                            a fölötte lévőket is elvinné.
                          */}
                          <Button
                            size="small"
                            color="warning"
                            disabled={
                              szintMuvelet ||
                              !legfelso ||
                              level.folderCount > 0 ||
                              loc.levels!.length <= MIN_SHELF_LEVELS
                            }
                            onClick={() =>
                              removeLevel.mutate({
                                barcode: loc.barcode,
                                levelNumber: level.levelNumber,
                              })
                            }
                          >
                            Törlés
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" spacing={2} sx={{ p: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              disabled={szintMuvelet || loc.levels.length >= MAX_SHELF_LEVELS}
              onClick={() => addLevel.mutate({ barcode: loc.barcode })}
            >
              Új szint hozzáadása
            </Button>
            <Typography variant="caption" color="text.secondary">
              A polcnak legalább {MIN_SHELF_LEVELS}, legfeljebb {MAX_SHELF_LEVELS} szintje lehet. A
              lezárt szintre betárolni nem lehet — a rajta lévő mappák viszont bármikor kivehetők,
              hogy egy elrontott lezárás ne rekesszen bent semmit.
            </Typography>
          </Stack>
        </Paper>
      )}

      {loc.pallet && (
        <Paper variant="outlined">
          <Stack direction="row" spacing={2} sx={{ p: 2, pb: 1, alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              Raklap — {loc.levels?.length ?? 0} szint
            </Typography>
            <Chip size="small" variant="outlined" label={PALLET_STATUS_LABEL_HU[loc.pallet.status]} />
          </Stack>

          <Divider />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="right">Szint</TableCell>
                  <TableCell align="right">Mappák</TableCell>
                  <TableCell>Elérhetőség</TableCell>
                  <TableCell align="right">Műveletek</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {szintekFentrolLefele.map((level) => (
                  <TableRow
                    key={level.levelNumber}
                    hover
                    selected={szintSzuro === level.levelNumber}
                    sx={{ cursor: 'pointer' }}
                    onClick={() =>
                      setSzintSzuro((elozo) =>
                        elozo === level.levelNumber ? null : level.levelNumber,
                      )
                    }
                  >
                    <TableCell align="right">{level.levelNumber}</TableCell>
                    <TableCell align="right">{level.folderCount}</TableCell>
                    <TableCell>{ACCESSIBILITY_LABEL_HU[level.accessibility]}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          color="warning"
                          disabled={level.folderCount === 0}
                          onClick={() =>
                            setDismantle({
                              levelNumber: level.levelNumber,
                              folderCount: level.folderCount,
                            })
                          }
                        >
                          Mappák áthelyezése
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" spacing={2} sx={{ p: 2, justifyContent: 'space-between' }}>
            {/*
              „Hozzáadás", nem „nyitás" (2026-08-14-i CR #10): a szintnek a
              2026-08-13-i CR 1.1 óta nincs nyitott/zárt állapota, a régi
              szóhasználat viszont azt sugallta, hogy a megszüntetett funkció
              még él.
            */}
            <Button
              variant="outlined"
              disabled={szintMuvelet || (loc.levels?.at(-1)?.folderCount ?? 0) === 0}
              onClick={() => addLevel.mutate({ barcode: loc.barcode })}
            >
              Új szint hozzáadása
            </Button>
            <Button
              color="warning"
              variant="outlined"
              disabled={loc.folderCount === 0}
              onClick={() => setDismantle({ folderCount: loc.folderCount })}
            >
              Minden mappa áthelyezése ({loc.folderCount} mappa)
            </Button>
          </Stack>

          {/*
            A gomb akkor is tiltott, ha a legfelső szint üres — ugyanaz a
            szabály, amit a szerver zárolás alatt kikényszerít. A tiltás itt
            csak MAGYARÁZAT, nem védelem: a felhasználó lássa, miért nem
            lehet, ahelyett hogy hibaüzenetbe futna.
          */}
          {(loc.levels?.at(-1)?.folderCount ?? 0) === 0 && (
            <Typography variant="caption" sx={{ px: 2, pb: 2, display: 'block' }} color="text.secondary">
              Új szint csak a legfelső fölé nyitható, és csak akkor, ha azon már van mappa —
              elválasztó lemez üres szintre nem kerül.
            </Typography>
          )}

          {addLevel.isError && (
            <div style={{ padding: 16 }}>
              <ProblemAlert error={addLevel.error} />
            </div>
          )}
        </Paper>
      )}

      {/*
        `key`: másik lokációra navigálva a komponens ÚJRA létrejön, tehát az
        űrlap a friss adatból indul. Enélkül effektben kellene állapotot
        írni, ami kaszkádoló újrarajzolást okoz (és amit a linter is tilt).
      */}
      <EditLocationCard key={loc.barcode} location={loc} />

      {/* Raklaptörténet (US-PALLET-ACTION-4) — csak raklapnál értelmezett. */}
      {loc.pallet && (
        <Paper variant="outlined">
          <Typography variant="subtitle1" sx={{ p: 2, pb: 1 }}>
            Raklaptörténet
          </Typography>
          <Divider />
          {palletHistory.isError && <ProblemAlert error={palletHistory.error} />}
          <EventTable
            events={palletHistory.data ?? []}
            emptyText="Még nincs raklap-esemény."
          />
        </Paper>
      )}

      <DismantleDialog
        open={dismantle !== null}
        onClose={() => setDismantle(null)}
        palletBarcode={loc.barcode}
        levelNumber={dismantle?.levelNumber}
        folderCount={dismantle?.folderCount ?? 0}
      />

      <Paper variant="outlined">
        <Stack direction="row" spacing={2} sx={{ p: 2, pb: 1, alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            {szintSzuro === null
              ? `Itt lévő mappák (${szurtMappak.length})`
              : `${szintSzuro}. szint tartalma (${szurtMappak.length})`}
          </Typography>
          {szintSzuro !== null && (
            <Button size="small" onClick={() => setSzintSzuro(null)}>
              Minden szint
            </Button>
          )}
        </Stack>
        {loc.levels && loc.levels.length > 0 && szintSzuro === null && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1, display: 'block' }}>
            A fenti táblázatban egy szintre kattintva csak annak a tartalmát mutatjuk.
          </Typography>
        )}
        <Divider />
        {folders.isError && <ProblemAlert error={folders.error} />}
        <TableContainer sx={{ maxHeight: 420 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Vonalkód</TableCell>
                <TableCell>Gerincszöveg</TableCell>
                <TableCell align="right">Szint</TableCell>
                <TableCell>Mióta</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {szurtMappak.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {szintSzuro === null ? 'Nincs itt mappa.' : `A(z) ${szintSzuro}. szint üres.`}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {szurtMappak.map((f) => (
                <TableRow key={f.barcode} hover>
                  {/* A vonalkód VIGYEN a mappára — eddig holt szöveg volt. */}
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    <Link component={RouterLink} to={`/mappak/${f.barcode}`}>
                      {f.barcode}
                    </Link>
                  </TableCell>
                  <TableCell>{f.currentText ?? '—'}</TableCell>
                  <TableCell align="right">{f.palletLevel ?? '—'}</TableCell>
                  <TableCell>{formatTimestamp(f.since)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
}

/**
 * A módosító űrlap külön komponens, hogy a szülő `key`-jel újra tudja
 * teremteni — így a helyi állapot mindig a friss szerveradatból indul,
 * effektbeli setState nélkül.
 */
function EditLocationCard({ location: loc }: { location: LocationView }) {
  const update = useUpdateLocation();
  const [name, setName] = useState(loc.name);
  const [notes, setNotes] = useState('');

  const isInactive = loc.status === 'INACTIVE';

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle1">Módosítás</Typography>

        {update.isError && <ProblemAlert error={update.error} />}

        <TextField label="Név" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
        <TextField
          label="Megjegyzés hozzáfűzése (opcionális)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />

        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            disabled={update.isPending || name.trim().length === 0 || name === loc.name}
            onClick={() =>
              update.mutate({
                barcode: loc.barcode,
                name: name.trim(),
                notes: notes.trim() || undefined,
              })
            }
          >
            Mentés
          </Button>

          {isInactive ? (
            <Button
              variant="outlined"
              color="success"
              disabled={update.isPending}
              onClick={() => update.mutate({ barcode: loc.barcode, status: 'ACTIVE' })}
            >
              Aktiválás
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="warning"
              disabled={update.isPending || loc.isDefaultStaging || loc.folderCount > 0}
              onClick={() => update.mutate({ barcode: loc.barcode, status: 'INACTIVE' })}
            >
              Inaktiválás
            </Button>
          )}
        </Stack>

        {!isInactive && loc.folderCount > 0 && (
          <Typography variant="caption" color="text.secondary">
            Az inaktiválás csak üres lokációnál lehetséges — jelenleg {loc.folderCount} mappa van
            itt. Előbb helyezd át őket.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" sx={{ minWidth: 160, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
}
